import type { PaymentMethod, PaypalSupplementaryData } from 'constants/payment'
import {
  SHORT_LEAD_TIME_WITH_FLYING_BLUE_LEAD_TIME_DAYS, HIGH_RISK_LEAD_TIME_FOR_MAJORITY_CREDIT_IN_DAYS, HIGH_RISK_TRANSACTION_CREDIT_PERCENTAGE_THRESHOLD, MAX_STRIPE_CUSTOM_PAYTO_PAYMENT, PAYMENT_OPTIONS, RAZORPAY_PAYMENT_TYPE, SHORT_LEAD_TIME_WITH_GOOGLE_PAY_LEAD_TIME_DAYS, SHORT_LEAD_TIME_WITH_PAYPAL_LEAD_TIME_DAYS, STRIPE_PAYMENT_ELEMENT_KLARNA_TYPE, PAYMENT_RESTRICTED_CURRENCIES_FOR_SPOOFED,
  STRIPE_TERMINAL_STORE_UTMS,
  STRIPE_TERMINAL_PAYMENT_TYPE,
  STRIPE_PAYMENT_ELEMENT_LINK_TYPE,
  PAYMENT_CHECKOUT_TYPES,
} from 'constants/payment'
import { createSelector } from 'reselect'
import { isPrestige4nPromo } from 'checkout/constants/paymentPromotions'
import { getFlightItems, isStandaloneFlights } from 'checkout/selectors/view/flights'
import { cartIncludesTourV2 } from './toursv2'
import {
  isStripePaymentElementGiropayEnabled,
  isStripeCustomPaytoEnabled,
  isStripePaymentElementCardEnabled,
  isStripePaymentElementIDealEnabled,
  isStripePaymentElementKlarnaEnabled,
  isStripePaymentElementPayNowEnabled,
  isStripePaymentElementGrabPayEnabled,
  isStripePaymentElementWeChatPayEnabled,
} from 'lib/payment/stripeUtils'
import { isGooglePayEnabled, isGooglePayEnabledBroadened } from 'lib/payment/googlePayUtils'
import { isStandaloneExperiences } from 'checkout/selectors/payment/experience'
import { getAccommodationItems } from 'checkout/selectors/view/accommodation'
import { getCarHireItems } from 'checkout/selectors/view/carHire'
import { isLEStaff } from 'selectors/accountSelectors'
import { findPostPurchaseCheckout, isBedbankItem, isCruiseItem, isGiftCardItem, isPurchaseCreditItem, isLEAccommodationItem, isLEHotelItem, isTourV1Item, isTourV2Item, isNoProtectionInsuranceItem, isFlightItem, isMerchandiseItem, mapCartModeToCheckoutType } from 'lib/checkout/checkoutUtils'
import getPaymentMethods, { getPaymentMethodLimitsByRegion, getPaymentMethodsByRegion } from 'lib/payment/getPaymentMethods'
import getPayableAfterModifiers from './getPayableAfterModifiers'
import getCheckoutTotal from 'checkout/selectors/payment/getCheckoutTotal'
import getPayableTotal from 'checkout/selectors/payment/getPayableTotal'
import getCheckoutTotalsView from 'checkout/selectors/payment/getCheckoutTotalsView'
import { getCartHasPropertyCollectItem } from './propertyCollectSelectors'
import {
  isBraintreeEnabled,
  isKlarnaEnabled,
  isWithinKlarnaAvailabilityRange,
  isKlarnaBpEnabled,
  isWithinKlarnaBpAvailabilityRange,
  getProjectedLeadTimeDaysByCartItems,
  isPayPalEnabled,
} from 'lib/payment/paymentUtils'
import { isAfterPayBpEnabled, isWithinAfterPayBpAvailabilityRange } from 'lib/payment/afterPayBpUtils'
import { isHoolahBpEnabled, isWithinHoolahBpAvailabilityRange } from 'lib/payment/hoolahBpUtils'
import { isApplePayEnabled, isApplePayEnabledBroadened } from 'lib/payment/applePayUtils'
import { isAtomeBpEnabled, isWithinAtomeBpAvailabilityRange } from 'lib/payment/atomeBpUtils'
import { isWaaveBpEnabled, isWithinWaaveBpAvailabilityRange } from 'lib/payment/waaveBpUtils'
import config from 'constants/config'
import { isSpoofed, isVelocityEnabled } from 'selectors/featuresSelectors'
import { getVelocityEligibleItems } from './partnerships'
import { VIRGIN_VELOCITY_CHECKOUT_PAYMENT_METHODS } from 'constants/partnerships'
import { mapObject } from 'lib/object/objectUtils'
import getCreditPayableAmount from 'checkout/selectors/payment/getCreditPayableAmount'
import moment from 'moment'
import shouldShowGiftCards from 'lib/payment/shouldShowGiftCards'
import { isLuxPlusSubscriptionInCart, isStandaloneLuxPlusSubscription } from '../view/luxPlusSubscription'
import { isLoyaltyPurchasePointsInCart } from '../view/loyaltyPurchasePoints'
import { getVillaItems } from '../view/villa'
import { isSecurePaymentEnabled } from 'lib/payment/securePaymentUtils'
import { isCruiseCheckout } from '../view/cruise'
import { isPaymentTypeFlyingBlueEnabled } from 'lib/payment/flyingblueUtils'
import { isPaymentTypeAzupayPayIdEnabled } from 'lib/payment/azupayUtils'
import { isPostPurchaseAncillaryPayment } from './isPostPurchaseAncillaryPayment'
import { Experiments } from 'constants/experiments'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { getOptimizelyExperimentVariation, getExperimentEnabledDecision, getOptimizelyFeatureFlagVariation } from 'lib/optimizely/optimizelyUtils'
import getExperienceItems from '../view/items/getExperienceItems'
import getTransferItems from '../view/items/getTransferItems'
import getMerchandiseItems from '../view/items/getMerchandiseItems'
import { getCheckoutLoyaltyBurnTotalInPoints } from '../loyalty/getCheckoutLoyaltyBurnTotals'
import getGiftCardPayableAmount from './getGiftCardPayableAmount'
import { isExpressCheckoutEnabled } from 'lib/payment/expressCheckoutUtils'
import { getGiftCardItems } from '../view/giftCard'
import { isCustomOfferCheckout, isPostPurchaseCustomOffer } from './checkout'
import { isSecurePaymentPage } from 'selectors/securePaymentSelectors'
import { isReserveForFreeSelected } from './paymentUsageSelectors'

export { isReserveForFreeSelected }

export const isDepositSelected = (state: App.State) => state.checkout.payment.paymentSelected === PAYMENT_OPTIONS.DEPOSIT

export const isPaymentScheduleSelected = (state: App.State) => state.checkout.payment.paymentSelected === PAYMENT_OPTIONS.PAYMENT_SCHEDULE

export const isDynamicPaymentScheduleSelected = (state: App.State) => state.checkout.payment.paymentSelected === PAYMENT_OPTIONS.DYNAMIC_PAYMENT_SCHEDULE

export const isGroupPaymentSelected = (state: App.State) => state.checkout.payment.paymentSelected === PAYMENT_OPTIONS.GROUP_PAYMENT

export const isFullPaymentSelected = (state: App.State) => state.checkout.payment.paymentSelected === PAYMENT_OPTIONS.FULL

export const getCheckoutCurrency = (state: App.State) => state.checkout.cart.currencyCode

export const isRazorpayAvailable = createSelector(
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => isStandaloneFlights(state),
  (state: App.State) => cartIncludesTourV2(state), // Temporarily disabled for tours, until it can be tested
  (currency, isStandaloneFlights, includesTourV2) => !includesTourV2 && getPaymentMethods(currency).includes(RAZORPAY_PAYMENT_TYPE) && !isStandaloneFlights,
)

export const isStripePaymentElementCardAvailable = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => isRazorpayAvailable(state),
  (regionCode, currencyCode, razorpayAvailable) => {
    return !!isStripePaymentElementCardEnabled(regionCode, currencyCode) && !razorpayAvailable
  },
)

const isGiftCardPurchase = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): boolean => items.length > 0 && items.some(isGiftCardItem),
)

const isPurchaseCreditPurchase = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): boolean => items.length > 0 && items.some(isPurchaseCreditItem),
)

const isGiftCardOrCreditPurchase = createSelector(
  (state: App.State) => isGiftCardPurchase(state),
  (state: App.State) => isPurchaseCreditPurchase(state),
  (isGiftCard, isPurchaseCredit): boolean => isGiftCard || isPurchaseCredit,
)

export const isGiftCardEnabled = createSelector(
  (state: App.State) => getCheckoutTotal(state),
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => isGiftCardOrCreditPurchase(state),
  (state: App.State) => isStandaloneFlights(state),
  (state: App.State) => getCartHasMerchandiseItem(state),
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (state: App.State) => isLoyaltyPurchasePointsInCart(state),
  (state: App.State) => isReserveForFreeSelected(state),
  (state: App.State) => state.checkout.payment.partnerships.velocity?.burn?.isApplied,
  (
    payableTotal,
    currencyCode,
    isGiftCard,
    isStandaloneFlight,
    hasMerchandiseItem,
    isLuxPlusSubscriptionInCart,
    isLoyaltyPurchasePointsInCart,
    isReserveForFreeSelected,
    isVelocityApplied,
  ): boolean => {
    // Disables gift card if there is no payment
    if (!payableTotal) return false

    return shouldShowGiftCards(currencyCode) &&
      !isGiftCard &&
      !isStandaloneFlight &&
      !hasMerchandiseItem &&
      !isLuxPlusSubscriptionInCart &&
      !isLoyaltyPurchasePointsInCart &&
      !isReserveForFreeSelected &&
      !isVelocityApplied
  },
)

const getGrandTotal = (state: App.State) => getCheckoutTotalsView(state).data.grandTotal

const getClosestProjectedLeadTimeDays = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (cartItems) => getProjectedLeadTimeDaysByCartItems(cartItems),
)

export const isGooglePayAvailable = createSelector(
  (state: App.State) => getCheckoutCurrency(state) || state.geo.currentCurrency,
  (state: App.State) => state.system.canPayWithGooglePay,
  (state: App.State) => getClosestProjectedLeadTimeDays(state),
  (state: App.State) => getOptimizelyExperimentVariation(state, Experiments.croExpressCheckoutAutoDefault.id),
  (currency, canPayWithGooglePay, closestProjectedLeadTimeDays, experimentVariation): boolean => {
    // Use broadened detection (all Android browsers) when the express checkout experiment is active;
    // fall back to narrow detection (Chrome-only) otherwise.
    const enabled = getExperimentEnabledDecision(experimentVariation)
      ? isGooglePayEnabledBroadened(currency)
      : isGooglePayEnabled(currency)
    return enabled &&
      canPayWithGooglePay &&
      // Adding card to Google pay does not always enforce OTP, high chance of fraud
      closestProjectedLeadTimeDays >= SHORT_LEAD_TIME_WITH_GOOGLE_PAY_LEAD_TIME_DAYS &&
      isExpressCheckoutEnabled(currency)
  },
)

export const isApplePayAvailable = createSelector(
  (state: App.State) => getCheckoutCurrency(state) || state.geo.currentCurrency,
  (state: App.State) => getOptimizelyExperimentVariation(state, Experiments.croExpressCheckoutAutoDefault.id),
  (currency, experimentVariation): boolean => {
    // Use broadened detection (all iOS browsers) when the express checkout experiment is active;
    // fall back to narrow detection (Safari-only) otherwise.
    const enabled = getExperimentEnabledDecision(experimentVariation)
      ? isApplePayEnabledBroadened(currency)
      : isApplePayEnabled(currency)
    return enabled && isExpressCheckoutEnabled(currency)
  },
)

export const isLinkAvailable = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => getCheckoutCurrency(state) || state.geo.currentCurrency,
  (state: App.State) => isDepositSelected(state),
  (state: App.State) => isPaymentScheduleSelected(state),
  (state: App.State) => isDynamicPaymentScheduleSelected(state),
  (state: App.State) => isGroupPaymentSelected(state),
  (state: App.State) => isFullPaymentSelected(state),
  (state: App.State) => isSpoofed(state),
  (state: App.State) => isSecurePaymentPage(state),
  (regionCode, currencyCode, isDeposit, isPaymentSchedule, isDynamicPaymentSchedule, isGroupPayment, isFullPayment, isSpoofed, isSecurePaymentPage): boolean => {
    if (!config.STRIPE_PAYMENT_ELEMENT_LINK_ENABLED) return false
    if (isSecurePaymentPage) return false
    const isLinkEnabledForRegionOrCurrency = getPaymentMethodsByRegion(regionCode).includes(STRIPE_PAYMENT_ELEMENT_LINK_TYPE) ||
      getPaymentMethods(currencyCode).includes(STRIPE_PAYMENT_ELEMENT_LINK_TYPE)
    if (!isLinkEnabledForRegionOrCurrency) return false
    return (isDeposit || isPaymentSchedule || isDynamicPaymentSchedule || isGroupPayment || isFullPayment) && !isSpoofed
  },
)

/**
 * Société Rewards on Maldives Charters: redemptions that require a fixed points amount plus a
 * co-payment. These are normal flight items with `isFlightClassicReward`. Promo codes and wallet
 * vouchers must not apply on these offers.
 */

export const isClassicRewardOrder = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (state: App.State) => getVillaItems(state),
  (state: App.State) => getFlightItems(state),
  (state: App.State) => getCarHireItems(state),
  (state: App.State) => getTransferItems(state),
  (state: App.State) => getExperienceItems(state),
  (state: App.State) => getMerchandiseItems(state),
  (accommodationItems,
    villaItems,
    flightItems,
    carHireItems,
    transferItem,
    experienceItems,
    merchandiseItems): boolean =>
    accommodationItems.some((a) => 'isClassicReward' in a && a.isClassicReward)
    || villaItems.some((v) => 'isClassicReward' in v && v.isClassicReward)
    || flightItems.some((fi) => fi.isClassicReward)
    || carHireItems.some((c) => 'isClassicReward' in c && c.isClassicReward)
    || transferItem.some((t) => 'isClassicReward' in t && t.isClassicReward)
    || experienceItems.some((e) => 'isClassicReward' in e && e.isClassicReward)
    || merchandiseItems.some((m) => 'isClassicReward' in m && m.isClassicReward),

)

export const isAllowedPromoOrVoucher = createSelector(
  (state: App.State) => isClassicRewardOrder(state),
  (isClassicRewardOrder): boolean =>
    !isClassicRewardOrder,
)

function hasZeroPricedAdultOrChildCharterLeg(item: App.Checkout.FlightItem): boolean {
  if (!item.charterBundleRoute) {
    return false
  }

  const hasAdultPassenger = item.passengers.some(passenger => passenger.type === 'adult')
  const hasChildPassenger = item.passengers.some(passenger => passenger.type === 'child')

  return item.flights.some(flight =>
    (hasAdultPassenger && flight.fareFamily?.cost.adult === 0) ||
    (hasChildPassenger && flight.fareFamily?.cost.children === 0),
  )
}

export const isPromoCodeAllowed = createSelector(
  (state: App.State) => getCheckoutTotal(state),
  (state: App.State) => isStandaloneExperiences(state),
  (state: App.State) => getAccommodationItems(state),
  (state: App.State) => getVillaItems(state),
  (state: App.State) => getFlightItems(state),
  (state: App.State) => getCarHireItems(state),
  (state: App.State) => getTransferItems(state),
  (state: App.State) => state.checkout.payment.partnerships,
  (state: App.State) => isLEStaff(state),
  (state: App.State) => isPostPurchaseAncillaryPayment(state),
  (state: App.State) => state.checkout.cart.mode === 'change-package',
  (state: App.State) => isStandaloneLuxPlusSubscription(state),
  (state: App.State) => getExperienceItems(state),
  (state: App.State) => isSpoofed(state),
  (state: App.State) => getMerchandiseItems(state),
  (state: App.State) => getGiftCardItems(state),
  (state: App.State) => isAllowedPromoOrVoucher(state),
  (payableTotal,
    isStandaloneExperiences,
    accommodationItems,
    villaItems,
    flightItems,
    carHireItems,
    transferItem,
    partnerships,
    isLEStaff,
    isAncillaryPayment,
    isChangePackage,
    isStandaloneLuxPlusSubscription,
    experienceItems,
    isSpoofed,
    merchandiseItems,
    giftCardItems,
    allowedPromoOrVoucher,
  ): boolean => {
    const isExperience = experienceItems.length > 0

    if (!config.PROMO_CODES_ENABLED) {
      return false
    }

    // Disables promo code if there is no payment
    if (!payableTotal) {
      return false
    }
    if (
      partnerships?.velocity?.burn?.isApplied ||
      isAncillaryPayment ||
      isChangePackage
    ) {
      return false
    }

    // Promo codes are not allowed for Friends & Family bookings (unless spoofed or fnfPromoCodeApplicable)
    if (
      !isSpoofed &&
      flightItems.some(item => item.isFnFBooking && !item.fnfPromoCodeApplicable)
    ) {
      return false
    }

    if (flightItems.some(item => item.isPromotionPriceApplied)) {
      return false
    }

    if (!isSpoofed && flightItems.some(hasZeroPricedAdultOrChildCharterLeg)) {
      return false
    }

    if (!allowedPromoOrVoucher) {
      return false
    }

    return isExperience || isStandaloneExperiences ||
      isStandaloneLuxPlusSubscription ||
      (accommodationItems.length > 0 && accommodationItems.every(item => isLEAccommodationItem(item) || isBedbankItem(item) || isTourV1Item(item) || isTourV2Item(item) || isCruiseItem(item))) ||
      (flightItems.length > 0 && flightItems.every(item => isFlightItem(item))) ||
      ((isLEStaff || isSpoofed) && carHireItems.length > 0) ||
      ((isLEStaff || isSpoofed) && merchandiseItems.length > 0) ||
      (villaItems.length > 0) ||
      (transferItem.length > 0) ||
      (giftCardItems.length > 0)
  },
)

const cartHasBedbankItem = (state: App.State) => state.checkout.cart.items.some(item => item.itemType === 'bedbankHotel')

// TODO: implement- (copied this function over from checkoutPaymentSelectors, looks like it's been unimplemented for a long time)
const isBookDatesOnly = (_state: App.State) => false

/**
* @deprecated Will be removed once new PayPal integration is completed
*/
const isBraintreeAvailable = createSelector(
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => isGiftCardOrCreditPurchase(state),
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (state: App.State) => state.system.rawUserAgentString,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (currency, isGiftCard, isLuxPlusSubscriptionInCart, userAgentBrowserName, isPostPurchase) => {
    return isBraintreeEnabled(currency) &&
      !isGiftCard &&
      !isLuxPlusSubscriptionInCart &&
      !userAgentBrowserName?.toLowerCase().includes('mobileapp-android') && // Can be removed in early 2024 when paypal sdk upgraded on customer portal

      // We need to temporarily hide PayPal for change dates.
      // Paypal payments are breaking when when combined with credits for the creation of the new reservation.
      // Payments team have a ticket to fix this.  This code can be reverted when completed.
      // https://aussiecommerce.atlassian.net/browse/PP-2212
      // disable paypal flow for postpurchase due to payment issues, revisit when paypal commerce platform is used instead of braintree
      !isPostPurchase
  },
)

export const isPayPalAvailable = createSelector(
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => isGiftCardOrCreditPurchase(state),
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getGrandTotal(state),
  (state: App.State) => getCheckoutTotalsView(state).data.amountPayableByCredit,
  (state: App.State) => getClosestProjectedLeadTimeDays(state),
  (state: App.State) => state.checkout.payment.useCredit,
  (state: App.State) => getCheckoutLoyaltyBurnTotalInPoints(state),
  (state: App.State) => isCustomOfferCheckout(state),
  (state: App.State) => isSpoofed(state),
  (state: App.State) => isFullPaymentSelected(state),
  (state: App.State) => isPaymentScheduleSelected(state) || isDynamicPaymentScheduleSelected(state) || isGroupPaymentSelected(state),
  () => config.LUXURY_PLUS_PAYPAL_PAYMENT_ENABLED_CURRENCIES,
  () => config.PAYMENT_SCHEDULE_PAYPAL_PAYMENT_ENABLED,
  (currency, isGiftCard, isLuxPlusSubscriptionInCart, postPurchaseMode, grandTotal, amountPayableByCredit, closestProjectedLeadTimeDays, useCredit, loyaltyBurnTotalInPoints, isCustomOffer, isSpoofed, isFullPayment, isPaymentSchedulePayment, luxPlusPayPalEnabledCurrencies, isPaymentSchedulePayPalEnabled) => {
    const creditPercentage = amountPayableByCredit / grandTotal
    const isHighRisk = useCredit && closestProjectedLeadTimeDays < HIGH_RISK_LEAD_TIME_FOR_MAJORITY_CREDIT_IN_DAYS && creditPercentage > HIGH_RISK_TRANSACTION_CREDIT_PERCENTAGE_THRESHOLD
    const isShortLeadTimePayPalOrder = closestProjectedLeadTimeDays < SHORT_LEAD_TIME_WITH_PAYPAL_LEAD_TIME_DAYS
    const isLuxPlusPayPalEnabledForCurrency = luxPlusPayPalEnabledCurrencies.includes(currency)

    return (isFullPayment || isPaymentSchedulePayment) &&
      isPayPalEnabled(currency) &&
      !isGiftCard &&
      (!isLuxPlusSubscriptionInCart || isLuxPlusPayPalEnabledForCurrency) &&
      (!isPaymentSchedulePayment || isPaymentSchedulePayPalEnabled) &&
      (!postPurchaseMode || isCustomOffer || postPurchaseMode === 'payment-schedule-balance') &&
      !isHighRisk &&
      !isShortLeadTimePayPalOrder &&
      loyaltyBurnTotalInPoints === 0 &&
      !isSpoofed
  },
)

export const isPayLaterAvailable = createSelector(
  (state: App.State) => isPayPalAvailable(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.system.payPalSettings,
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => getCheckoutTotalsView(state, 'paypal').data.payableNow,
  (isPayPalAvailable, isPostPurchase, payPalSettings, regionCode, paymentAmount) => {
    const regionSetting = payPalSettings?.payLaterSettings?.regionSettings[regionCode]
    if (isPostPurchase || !regionSetting || !isPayPalAvailable)
    {
      return false
    }

    return (regionSetting.minAmount <= paymentAmount && paymentAmount <= regionSetting.maxAmount && regionSetting.enabled)
  },
)

export const shouldShowPayLaterPaymentOption = createSelector(
  (state: App.State) => isPayLaterAvailable(state),
  (isPayLaterAvailable) => {
    return isPayLaterAvailable
  },
)

export const shouldShowPayLaterButton = createSelector(
  (state: App.State) => isPayLaterAvailable(state),
  (isPayLaterAvailable) => {
    // currently disabled
    return isPayLaterAvailable && false
  },
)

export const shouldShowDebitCardMerchantFees = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.offer.offers,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (cartItems, offers, postPurchaseMode) => {
    const hasFlashHotel = cartItems.some(item => {
      if (isLEHotelItem(item)) {
        const offer = offers[item.offerId]
        if (offer?.type === 'hotel') {
          return true
        }
      }
      return false
    })

    return hasFlashHotel && !postPurchaseMode
  },
)

export const isInternationalMerchantCardAvailable = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.checkout.stripePaymentMethod?.cardCountry,
  (state: App.State) => state.securePayment.stripePaymentMethod?.cardCountry,
  (currentRegionCode, checkoutCardCountry, securePaymentCardCountry) => {
    // Only enable international merchant fees when:
    // 1. Current region is AU
    // 2. Card country is not AU (i.e., international card)
    const cardCountry = checkoutCardCountry || securePaymentCardCountry
    return currentRegionCode === 'AU' && cardCountry && cardCountry !== 'AU'
  },
)

// For user with credits on the checkout page,
// the grand total could be > 35 while the app
// is fetching the user's credit balance (assumed
// to be 0). This causes Klarna Checkout to be
// mounted and immediately unmounted once the user
// data returns and causes the grand total to equal 0
const isKlarnaAvailable = createSelector(
  (state: App.State) => getGrandTotal(state),
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => cartHasBedbankItem(state),
  (state: App.State) => isStandaloneFlights(state),
  (state: App.State) => isStandaloneExperiences(state),
  (state: App.State) => isGiftCardOrCreditPurchase(state),
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (
    cartTotal,
    currency,
    hasBedbankItem,
    isStandaloneFlights,
    isStandaloneExperiences,
    isGiftCard,
    isLuxPlusSubscriptionInCart,
  ) => !hasBedbankItem &&
    isKlarnaEnabled(currency) &&
    isWithinKlarnaAvailabilityRange(cartTotal) &&
    !isStandaloneFlights &&
    !isStandaloneExperiences &&
    !isGiftCard &&
    !isLuxPlusSubscriptionInCart,
)

const isKlarnaBpAvailable = createSelector(
  (state: App.State) => getGrandTotal(state),
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => cartHasBedbankItem(state),
  (state: App.State) => isStandaloneFlights(state),
  (state: App.State) => isStandaloneExperiences(state),
  (state: App.State) => isGiftCardOrCreditPurchase(state),
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (
    cartTotal,
    currency,
    hasBedbankItem,
    isStandaloneFlights,
    isStandaloneExperiences,
    isGiftCard,
    isLuxPlusSubscriptionInCart,
  ) => !hasBedbankItem &&
    isKlarnaBpEnabled(currency) &&
    isWithinKlarnaBpAvailabilityRange(cartTotal, currency) &&
    !isStandaloneFlights &&
    !isStandaloneExperiences &&
    !isGiftCard &&
    !isLuxPlusSubscriptionInCart,
)

const isAfterPayBpAvailable = createSelector(
  (state: App.State) => getGrandTotal(state),
  (state: App.State) => isBookDatesOnly(state),
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => isStandaloneFlights(state),
  (state: App.State) => isGiftCardOrCreditPurchase(state),
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (
    cartTotal,
    bookDatesOnly,
    currency,
    isStandaloneFlights,
    isGiftCard,
    isLuxPlusSubscriptionInCart,
  ) => isAfterPayBpEnabled(currency) &&
    !bookDatesOnly &&
    isWithinAfterPayBpAvailabilityRange(cartTotal, currency) &&
    !isStandaloneFlights &&
    !isGiftCard &&
    !isLuxPlusSubscriptionInCart,
)

const isHoolahBpAvailable = createSelector(
  (state: App.State) => getGrandTotal(state),
  (state: App.State) => isBookDatesOnly(state),
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => cartIncludesTourV2(state), (state: App.State) => isStandaloneFlights(state),
  (state: App.State) => isGiftCardOrCreditPurchase(state),
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (
    cartTotal,
    bookDatesOnly,
    currency,
    includesTourV2,
    isStandaloneFlights,
    isGiftCard,
    isLuxPlusSubscriptionInCart,
  ) =>
    isHoolahBpEnabled(currency) &&
    !includesTourV2 &&
    !bookDatesOnly &&
    !isStandaloneFlights &&
    isWithinHoolahBpAvailabilityRange(cartTotal, currency) &&
    !isGiftCard &&
    !isLuxPlusSubscriptionInCart,
)

const isAtomeBpAvailable = createSelector(
  (state: App.State) => getGrandTotal(state),
  (state: App.State) => isBookDatesOnly(state),
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => cartIncludesTourV2(state),
  (state: App.State) => isStandaloneFlights(state),
  (state: App.State) => isGiftCardOrCreditPurchase(state),
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (
    cartTotal,
    bookDatesOnly,
    currency,
    includesTourV2,
    isStandaloneFlights,
    isGiftCard,
    isLuxPlusSubscriptionInCart,
  ) =>
    isAtomeBpEnabled(currency) &&
    !includesTourV2 &&
    !bookDatesOnly &&
    !isStandaloneFlights &&
    isWithinAtomeBpAvailabilityRange(cartTotal, currency) &&
    !isGiftCard &&
    !isLuxPlusSubscriptionInCart,
)

const isWaaveBpAvailable = createSelector(
  (state: App.State) => getGrandTotal(state),
  (state: App.State) => isBookDatesOnly(state),
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => isStandaloneFlights(state),
  (state: App.State) => cartIncludesTourV2(state),
  (state: App.State) => isGiftCardOrCreditPurchase(state),
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (
    cartTotal,
    bookDatesOnly,
    currency,
    isStandaloneFlights,
    includesTourV2,
    isGiftCard,
    isLuxPlusSubscriptionInCart,
  ) =>
    isWaaveBpEnabled(currency) &&
    !includesTourV2 &&
    !bookDatesOnly &&
    !isStandaloneFlights &&
    // !isChangePackage && // Add this here once the new checkout has package change feature available
    isWithinWaaveBpAvailabilityRange(cartTotal, currency) &&
    !!config.WAAVE_BP_GLOBAL_CHECKOUT_ENABLED &&
    !isGiftCard &&
    !isLuxPlusSubscriptionInCart,
)

export const isVelocityAvailable = createSelector(
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => isVelocityEnabled(state),
  (state: App.State) => getVelocityEligibleItems(state),
  (state: App.State) => isStandaloneFlights(state),
  (state: App.State) => isStandaloneExperiences(state),
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (state: App.State) => isSpoofed(state),
  (state: App.State) => state.checkout.payment.useCredit,
  (currency, isVelocityEnabled, velocityEligibleItems, isStandaloneFlights, isStandaloneExperiences, isLuxPlusSubscriptionInCart, isSpoofed, useCredit): boolean =>
    currency === 'AUD' &&
    isVelocityEnabled &&
    !isStandaloneFlights &&
    !isStandaloneExperiences &&
    velocityEligibleItems.length > 0 &&
    !isLuxPlusSubscriptionInCart &&
    !isSpoofed &&
    !useCredit,
)

const isStripePaymentElementGiropayAvailable = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (regionCode, isLuxPlusSubscriptionInCart): boolean =>
    !!isStripePaymentElementGiropayEnabled(regionCode) &&
    !isLuxPlusSubscriptionInCart,
)

export const isStripePaymentElementIDealAvailable = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (regionCode, currencyCode, isLuxPlusSubscriptionInCart): boolean =>
    currencyCode === 'EUR' &&
    !!isStripePaymentElementIDealEnabled(regionCode) &&
    !isLuxPlusSubscriptionInCart,
)

export const isStripePaymentElementWeChatPayAvailable = createSelector(
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => getPayableTotal(state),
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (currencyCode, payableTotal, isLuxPlusSubscriptionInCart): boolean =>
    currencyCode === 'CNY' &&
    !!isStripePaymentElementWeChatPayEnabled(currencyCode) &&
    payableTotal <= 50000 &&
    !isLuxPlusSubscriptionInCart,
)

export const isStripePaymentElementKlarnaAvailable = createSelector(
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (state: App.State) => isCruiseCheckout(state),
  (state: App.State) => getPayableTotal(state),
  (currencyCode, regionCode, isLuxPlusSubscriptionInCart, isCruiseInCart, payableTotal): boolean =>
    !!isStripePaymentElementKlarnaEnabled(currencyCode) &&
    !isLuxPlusSubscriptionInCart &&
    getPaymentMethodLimitsByRegion(regionCode)?.some(region => region.paymentMethod === STRIPE_PAYMENT_ELEMENT_KLARNA_TYPE && payableTotal <= region.limit) &&
    !isCruiseInCart,
)

export const isStripePaymentElementPayNowAvailable = createSelector(
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (currencyCode, isLuxPlusSubscriptionInCart): boolean =>
    !!isStripePaymentElementPayNowEnabled(currencyCode) &&
    !isLuxPlusSubscriptionInCart,
)

export const isStripePaymentElementGrabPayAvailable = createSelector(
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (currencyCode, isLuxPlusSubscriptionInCart): boolean =>
    !!isStripePaymentElementGrabPayEnabled(currencyCode) &&
    !isLuxPlusSubscriptionInCart,
)

export const isPaymentTypeFlyingBlueAvailable = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (state: App.State) => getClosestProjectedLeadTimeDays(state),
  (state: App.State) => isGiftCardOrCreditPurchase(state),
  (regionCode, currencyCode, isLuxPlusSubscriptionInCart, closestProjectedLeadTimeDays, isGiftCard): boolean =>
    currencyCode === 'EUR' &&
    !!isPaymentTypeFlyingBlueEnabled(regionCode) &&
    !isLuxPlusSubscriptionInCart &&
    closestProjectedLeadTimeDays > SHORT_LEAD_TIME_WITH_FLYING_BLUE_LEAD_TIME_DAYS &&
    !isGiftCard,
)

export const isPaymentTypeAzupayPayIdAvailable = createSelector(
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (state: App.State) => !!getOptimizelyFeatureFlagVariation(state, OptimizelyFeatureFlags.payIdTargetedRelease),
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => isCustomOfferCheckout(state),
  (currencyCode, isPostPurchase, isLuxPlusSubscriptionInCart, isAzupayPayIdOptimizelyEnabled, items, isCustomOffer): boolean =>
  {
    const relevantItems = items.filter(item => !isNoProtectionInsuranceItem(item))
    return !!isPaymentTypeAzupayPayIdEnabled(currencyCode) &&
      !isLuxPlusSubscriptionInCart &&
      (!isPostPurchase || isCustomOffer) &&
      !isAzupayPayIdOptimizelyEnabled &&
      !(relevantItems.length > 1)
  },
)

export const isStripeTerminalEnabled = createSelector(
  (state: App.State) => getCheckoutCurrency(state),
  (state: App.State) => state.utm.medium,
  (state: App.State) => state.auth.account.isSpoofed,
  (currencyCode, utmSource, isSpoofed): boolean => {
    const isStripeTerminalPaymentEnabled = getPaymentMethods(currencyCode).includes(STRIPE_TERMINAL_PAYMENT_TYPE)

    return isStripeTerminalPaymentEnabled && isSpoofed && STRIPE_TERMINAL_STORE_UTMS.includes(utmSource)
  },
)

export const cartIneligibleForStripeTerminal = createSelector(
  (state: App.State) => getCartHasPropertyCollectItem(state),
  (state: App.State) => isPostPurchaseAncillaryPayment(state),
  (cartHasPropertyCollectItem, isPostPurchase): boolean => {
    return cartHasPropertyCollectItem || isPostPurchase
  },
)

export const isStripeTerminalAvailable = createSelector(
  (state: App.State) => isStripeTerminalEnabled(state),
  (state: App.State) => cartIneligibleForStripeTerminal(state),
  (isEnabled, cartIneligibleForStripeTerminal): boolean => {
    return isEnabled && !cartIneligibleForStripeTerminal
  },
)

export const isStripeCustomPaytoAvailable = createSelector(
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (state: App.State) => getPayableAfterModifiers(state),
  (currencyCode, isLuxPlusSubscriptionInCart, payableAfterModifiers): boolean =>
    currencyCode === 'AUD' &&
    !!(isStripeCustomPaytoEnabled(currencyCode)) &&
    payableAfterModifiers <= MAX_STRIPE_CUSTOM_PAYTO_PAYMENT &&
    !isLuxPlusSubscriptionInCart,
)

const isGoCardlessAvailable = () => false // cleanup required

export const isPaymentRestrictedForCurrencyAndSpoofed = createSelector(
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => state.auth.account.isSpoofed,
  (currencyCode, isSpoofed): boolean =>
    isSpoofed && PAYMENT_RESTRICTED_CURRENCIES_FOR_SPOOFED.includes(currencyCode),
)

export const isSecurePaymentAvailable = createSelector(
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => state.auth.account.isSpoofed,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.checkout.cart.items,
  (currencyCode, isSpoofed, postPurchase, items): boolean =>
    isSpoofed &&
    !postPurchase &&
    isSecurePaymentEnabled(currencyCode) &&
    !items.some(isTourV1Item),
)

export const getPaymentMethodAvailability = createSelector(
  (state: App.State) => isFullPaymentSelected(state),
  (state: App.State) => shouldShowPayLaterPaymentOption(state),
  (state: App.State) => state.checkout.promo,
  (state: App.State) => isBraintreeAvailable(state),
  (state: App.State) => isPayPalAvailable(state),
  (state: App.State) => isRazorpayAvailable(state),
  (state: App.State) => isKlarnaAvailable(state),
  (state: App.State) => isKlarnaBpAvailable(state),
  (state: App.State) => isAfterPayBpAvailable(state),
  (state: App.State) => isHoolahBpAvailable(state),
  (state: App.State) => isAtomeBpAvailable(state),
  (state: App.State) => isWaaveBpAvailable(state),
  (state: App.State) => isVelocityAvailable(state),
  (state: App.State) => isStripePaymentElementCardAvailable(state),
  (state: App.State) => isStripePaymentElementGiropayAvailable(state),
  (state: App.State) => isStripePaymentElementIDealAvailable(state),
  (state: App.State) => isStripePaymentElementKlarnaAvailable(state),
  (state: App.State) => isStripePaymentElementWeChatPayAvailable(state),
  (state: App.State) => isStripePaymentElementPayNowAvailable(state),
  (state: App.State) => isStripePaymentElementGrabPayAvailable(state),
  (state: App.State) => isStripeCustomPaytoAvailable(state),
  (_: App.State) => isGoCardlessAvailable(),
  (state: App.State) => state.checkout.payment.partnerships.velocity?.burn?.isApplied,
  (state: App.State) => isReserveForFreeSelected(state),
  (state: App.State) => isSecurePaymentAvailable(state),
  (state: App.State) => isPaymentTypeFlyingBlueAvailable(state),
  (state: App.State) => isPaymentTypeAzupayPayIdAvailable(state),
  (state: App.State) => isPaymentScheduleSelected(state),
  (state: App.State) => isDynamicPaymentScheduleSelected(state),
  (state: App.State) => isGroupPaymentSelected(state),
  (state: App.State) => isApplePayAvailable(state),
  (state: App.State) => isGooglePayAvailable(state),
  (state: App.State) => getCartHasPropertyCollectItem(state),
  (state: App.State) => isPaymentRestrictedForCurrencyAndSpoofed(state),
  (state: App.State) => isStripeTerminalAvailable(state),
  (
    isFullPaymentSelected,
    shouldShowPayLaterPaymentOption,
    promotion,
    braintreeAvailable,
    paypalAvailable,
    razorpayAvailable,
    klarnaAvailable,
    klarnaBpAvailable,
    afterPayBpAvailable,
    hoolahBpAvailable,
    atomeBpAvailable,
    waaveBpAvailable,
    isVelocityAvailable,
    stripePaymentElementCardAvailable,
    stripePaymentElementGiropayAvailable,
    stripePaymentElementIDealAvailable,
    stripePaymentElementKlarnaAvailable,
    stripePaymentElementWeChatPayAvailable,
    stripePaymentElementPayNowAvailable,
    stripePaymentElementGrabPayAvailable,
    stripeCustomPaytoAvailable,
    goCardlessAvailable,
    isVelocityApplied,
    isReserveForFreeSelected,
    isSecurePaymentAvailable,
    flyingBlueAvailable,
    azupayPayIdAvailable,
    isPaymentScheduleSelected,
    isDynamicPaymentScheduleSelected,
    isGroupPaymentSelected,
    isApplePayAvailableBool,
    isGooglePayAvailableBool,
    cartHasPropertyCollectItem,
    isPaymentRestrictedForCurrencyAndSpoofed,
    stripeTerminalAvailable,
  ): Record<PaymentMethod, boolean> => {
    const paymentMethodAvailabilities: Record<string, boolean> = {

      stripe_payment_element_card: stripePaymentElementCardAvailable && !isPaymentRestrictedForCurrencyAndSpoofed,

      stripe_payment_element_giropay: stripePaymentElementGiropayAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      stripe_payment_element_ideal: stripePaymentElementIDealAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      stripe_payment_element_klarna: stripePaymentElementKlarnaAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      stripe_payment_element_wechatpay: stripePaymentElementWeChatPayAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      stripe_payment_element_paynow: stripePaymentElementPayNowAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      stripe_payment_element_grabpay: stripePaymentElementGrabPayAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,

      braintree: braintreeAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      paypal: paypalAvailable && !cartHasPropertyCollectItem,
      paylater: shouldShowPayLaterPaymentOption && isFullPaymentSelected && !cartHasPropertyCollectItem,
      razorpay: razorpayAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      klarna: klarnaAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      klarna_bp: klarnaBpAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      afterpay_bp: afterPayBpAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      hoolah_bp: hoolahBpAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      velocity: isVelocityAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      atome_bp: atomeBpAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      waave_bp: waaveBpAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      stripe_custom_payto: stripeCustomPaytoAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      gocardless: goCardlessAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      secure_payment: isSecurePaymentAvailable && !isReserveForFreeSelected && !isPaymentScheduleSelected && !isDynamicPaymentScheduleSelected && !isGroupPaymentSelected && !cartHasPropertyCollectItem,
      flying_blue: flyingBlueAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      azupay_pay_id: azupayPayIdAvailable && isFullPaymentSelected && !cartHasPropertyCollectItem,
      applepay: isApplePayAvailableBool && !cartHasPropertyCollectItem,
      googlepay: isGooglePayAvailableBool && !cartHasPropertyCollectItem,
      stripe_terminal: stripeTerminalAvailable && !cartHasPropertyCollectItem,
    }

    if (isVelocityApplied) {
      for (const key of Object.keys(paymentMethodAvailabilities)) {
        if (VIRGIN_VELOCITY_CHECKOUT_PAYMENT_METHODS.includes(key)) {
          continue
        }
        paymentMethodAvailabilities[key] = false
      }
    }

    const applyPromotionModifiers = promotion && promotion.allowedPaymentMethods.length > 0
    const applyPartnershipModifiers = isVelocityApplied

    // @ts-ignore
    return mapObject(paymentMethodAvailabilities, (value, payMethod: string) => {
      let result = value

      if (applyPromotionModifiers) {
        result = result && promotion.allowedPaymentMethods.includes(payMethod)
      }

      if (applyPartnershipModifiers) {
        result = result && VIRGIN_VELOCITY_CHECKOUT_PAYMENT_METHODS.includes(payMethod)
      }

      return result
    })
  },
)

export const getCartHasMerchandiseItem = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): boolean => items.some(isMerchandiseItem),
)

export const isCreditPayable = createSelector(
  (state: App.State) => getCreditPayableAmount(state),
  (state: App.State) => isStandaloneFlights(state),
  (state: App.State) => isGiftCardOrCreditPurchase(state),
  (state: App.State) => getCartHasPropertyCollectItem(state),
  (state: App.State) => state.checkout.payment.partnerships.velocity?.burn?.isApplied,
  (state: App.State) => getCartHasMerchandiseItem(state),
  (state: App.State) => isLoyaltyPurchasePointsInCart(state),
  (state: App.State) => isLEStaff(state),
  (state: App.State) => isSpoofed(state),
  (state: App.State) => state.checkout.promo?.code,
  (state: App.State) => state.checkout.cart,
  (state: App.State) => isPostPurchaseCustomOffer(state),
  (
    creditAmount,
    isStandaloneFlights,
    isGiftCardOrCreditPurchase,
    hasPropertyCollectItem,
    isVelocityBurnApplied,
    hasMerchandiseItem,
    hasLoyaltyPurchasePointsItem,
    isStaff,
    userIsSpoofed,
    promoCode,
    cart,
    isPostPurchaseCustomOfferItem,
  ): boolean => {
    const checkoutType = mapCartModeToCheckoutType(cart.mode)
    const alwaysBlockList = [
      isStandaloneFlights,
      isGiftCardOrCreditPurchase,
      hasPropertyCollectItem,
      hasLoyaltyPurchasePointsItem,
    ]

    // list restricted to internal staff or spoofed users
    const restrictedList = [
      hasMerchandiseItem,
    ]

    if (isPrestige4nPromo(promoCode)) {
      return false
    }

    if (creditAmount <= 0 || isVelocityBurnApplied) {
      return false
    }

    if (checkoutType === PAYMENT_CHECKOUT_TYPES.CustomOffer) {
      alwaysBlockList.push(isPostPurchaseCustomOfferItem)
    }

    if (alwaysBlockList.some(blocked => blocked)) {
      return false
    }

    if (restrictedList.some(restricted => restricted)) {
      return isStaff || userIsSpoofed
    }

    return true
  },
)

export const isGiftCardPayable = createSelector(
  (state: App.State) => getGiftCardPayableAmount(state),
  (state: App.State) => state.redeemedGiftCards.cards,
  (state: App.State) => isGiftCardOrCreditPurchase(state),
  (state: App.State) => getCartHasMerchandiseItem(state),
  (state: App.State) => isLoyaltyPurchasePointsInCart(state),
  (state: App.State) => isReserveForFreeSelected(state),
  (state: App.State) => state.checkout.payment.partnerships.velocity?.burn?.isApplied,
  (state: App.State) => getCartHasPropertyCollectItem(state),
  (giftCardPayableAmount, cards, isGiftCard, hasMerchandiseItem, hasLoyaltyPurchasePointsItem, isReserveForFreeSelected, isVelocityApplied, cartHasPropertyCollectItem): boolean => {
    return giftCardPayableAmount > 0 && cards.length > 0 && !isGiftCard && !hasMerchandiseItem && !hasLoyaltyPurchasePointsItem && !isReserveForFreeSelected && !isVelocityApplied && !cartHasPropertyCollectItem
  },
)

const toISO601Format = (date: string) => moment.utc(date).format('YYYY-MM-DDT hh:mm:ss.SSSZ')
export const getPaypalSupplementaryData = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.offer.bedbankOffers,
  (state: App.State) => state.offer.tourV2Offers,
  (cartItems, offers, bedbankOffers, tourV2Offers): PaypalSupplementaryData => {
    const leHotelItem = cartItems.find(isLEHotelItem)
    const bedbankItem = cartItems.find(isBedbankItem)
    const tourV1Item = cartItems.find(isTourV1Item)
    const tourV2Item = cartItems.find(isTourV2Item)

    if (tourV1Item) {
      const pkg = offers[tourV1Item.offerId]?.packages.find(p => p.id === tourV1Item.packageId)
      return {
        ota_service_start_date: toISO601Format(tourV1Item.startDate ?? ''),
        ota_service_end_date: toISO601Format(tourV1Item.endDate ?? ''),
        ota_start_country: pkg?.tour?.geoData.countryCode ?? undefined,
      }
    }
    else if (tourV2Item) {
      const offer = tourV2Offers[tourV2Item.offerId]
      const variation = offer?.variations[tourV2Item.purchasableOption.fkVariationId]
      return {
        ota_service_start_date: toISO601Format(tourV2Item.startDate),
        ota_service_end_date: toISO601Format(tourV2Item.endDate),
        ota_start_country: variation?.countriesVisited?.[0],
      }
    }
    else if (leHotelItem) {
      const offer = offers[leHotelItem.offerId]
      const supplementaryData = {
        ota_start_country: offer?.property?.geoData.countryCode,
      } as PaypalSupplementaryData
      if (leHotelItem.reservationType === 'instant_booking') {
        supplementaryData.ota_service_start_date = toISO601Format(leHotelItem.checkIn)
        supplementaryData.ota_service_end_date = toISO601Format(leHotelItem.checkOut)
      }
      return supplementaryData
    }
    else if (bedbankItem) {
      const offer = bedbankOffers[bedbankItem.offerId]
      return {
        ota_service_start_date: toISO601Format(bedbankItem.checkIn),
        ota_service_end_date: toISO601Format(bedbankItem.checkOut),
        ota_start_country: offer?.property.address.countryCode,
      }
    }
    return {}
  },
)

export const isAnyInstalmentPayAvailable = createSelector(
  (state: App.State) => isKlarnaAvailable(state),
  (state: App.State) => isAfterPayBpAvailable(state),
  (state: App.State) => isKlarnaBpAvailable(state),
  (state: App.State) => isHoolahBpAvailable(state),
  (state: App.State) => isAtomeBpAvailable(state),
  (klarna, afterPayBp, klarna_bp, hoolah_bp, atome_bp) => klarna || afterPayBp || klarna_bp || hoolah_bp || atome_bp,
)
